import moment from "moment";
import UtilityService from "./UtilityService";
import { postToAgents } from "./PlaybookApi";

const PlaybookLibraryService = {
	/**
	 * The agents this firm can run, its own and the built-in ones.
	 *
	 * @return {Promise<Array>}
	 */
	listPlaybooks() {
		return postToAgents({ operationId: "listPlaybooks", body: {} });
	},

	/**
	 * The categories agents sit under, with each one's icon, description and agent count.
	 *
	 * @return {Promise<Array>}
	 */
	listCategories() {
		return postToAgents({ operationId: "listPlaybookCategories", body: {} });
	},

	/**
	 * The categories as choices for a select, A to Z.
	 *
	 * @param {Object} params
	 * @param {Array} params.categories
	 * @return {Array} [{ value, label }]
	 */
	categoryChoices({ categories }) {
		const choices = [...(categories || [])]
			.sort((first, second) => first.name.localeCompare(second.name))
			.map(category => ({ value: category.id, label: category.name }));
		return choices;
	},

	/**
	 * The agents in one category, by name.
	 *
	 * @param {Object} params
	 * @param {Array} params.agents
	 * @param {Number} params.categoryId
	 * @return {Array}
	 */
	agentsInCategory({ agents, categoryId }) {
		const inCategory = agents.filter(agent => Number(agent.category_id) === Number(categoryId));
		inCategory.sort((first, second) => first.current_version.name.localeCompare(second.current_version.name));
		return inCategory;
	},

	/**
	 * The playbooks in a list that match a search (name or description) and a kind: all,
	 * built-in or the firm's own.
	 *
	 * @param {Object} params
	 * @param {Array} params.playbooks
	 * @param {String} params.search
	 * @param {String} params.kind all | builtin | custom
	 * @return {Array}
	 */
	playbooksMatching({ playbooks, search, kind }) {
		const searchText = UtilityService.toSearchText(search);
		const matching = playbooks.filter(playbook => {
			const version = playbook.current_version;
			const isKind = kind === "all" || (kind === "builtin" ? !!playbook.is_builtin : !playbook.is_builtin);
			if (!isKind) return false;
			if (!searchText) return true;
			const haystack = UtilityService.toSearchText(`${version.name || ""} ${version.description || ""}`);
			const isMatch = haystack.includes(searchText);
			return isMatch;
		});
		return matching;
	},

	/**
	 * When a playbook last ran on any client, in words: "Last run 2 hours ago", or that it never has.
	 *
	 * @param {Object} params
	 * @param {String|null} params.lastRunAt
	 * @return {String}
	 */
	describeLastRun({ lastRunAt }) {
		if (!lastRunAt) return "Never run";
		const described = `Last run ${moment(lastRunAt).fromNow()}`;
		return described;
	},

	/**
	 * Categories with something in them first, then the rest, each group A to Z — so a tab opens
	 * on what the firm already uses. The count read is the tab's: playbooks or skills.
	 *
	 * @param {Object} params
	 * @param {Array} params.categories
	 * @param {String} [params.countKey] agentCount | skillCount
	 * @return {Array}
	 */
	orderCategoriesForLibrary({ categories, countKey = "agentCount" }) {
		const byName = (first, second) => first.name.localeCompare(second.name);
		const inUse = categories.filter(category => category[countKey] > 0).sort(byName);
		const empty = categories.filter(category => !(category[countKey] > 0)).sort(byName);
		return [...inUse, ...empty];
	},

	/**
	 * The playbooks as picker choices: the current version's name, with its description as
	 * the help line, built-in ones marked. Every listed playbook carries its current version,
	 * and every version has a name.
	 *
	 * @param {Object} params
	 * @param {Array} params.playbooks from listPlaybooks
	 * @return {Array} [{ value, label, hint, description }]
	 */
	playbookChoices({ playbooks }) {
		const choices = (playbooks || []).map(playbook => {
			const version = playbook.current_version;
			return {
				value: String(playbook.id),
				label: version.name,
				hint: playbook.is_builtin ? "Built-in" : "",
				description: version.description || "",
			};
		});
		return choices;
	},
};

export default PlaybookLibraryService;
